/* ============================================================
   Brand-agnostic formula calculation engine.
   Pure functions only - no DOM, no fetch. Driven entirely by the
   product data loaded from data/powder-data.json and data/rtf-data.json.
   ============================================================ */

(function (global) {
  const DAYS_PER_WEEK = 7;
  const MONTHS_TO_WEEKS_FACTOR = 4.345;
  const BUFFER = 0.10;
  // RTF bottles ship shrink-wrapped in a fixed "Single Pack" size - any RTF
  // quantity must round up to a multiple of THAT product's own pack size to
  // actually be fulfillable. Most RTF lines ship 6 to a pack, but the
  // 90ml-bottle starters ship 24 to a pack instead (see
  // convert_data_v2.py's RTF_SHRINKWRAP_SIZE) - rounding a 90ml order to a
  // 6-multiple instead of its real 24-multiple would recommend an
  // unfulfillable quantity, so this is read per-stage, never hardcoded.
  // Powder has no such constraint: a real single-can SKU exists there, so
  // powder quantities stay exact aside from the minimum-order floor below
  // (see the "always use 1-pack" architecture decision).
  const DEFAULT_RTF_SHRINKWRAP_SIZE = 6;
  const POWDER_MIN_UNITS = 2;
  const EXTENSION_WEEKS = 104;

  // Below the smallest variant actually sold, the exact math (e.g. "1 can",
  // "3 bottles") isn't a real orderable quantity - floor it to whatever the
  // smallest tier for that stage is. Applied AFTER shrinkwrap rounding, not
  // instead of it, and only when there's a genuine need (units > 0) so a
  // stage the baby never reaches doesn't get bumped up from zero.
  function roundUnitsForPackaging(units, formulaKind, minUnits, shrinkwrapSize) {
    if (units === 0) return 0;
    const size = shrinkwrapSize || DEFAULT_RTF_SHRINKWRAP_SIZE;
    const rounded = formulaKind === "rtf" ? Math.ceil(units / size) * size : units;
    // The data carries a 1-can tier for the exact-quantity single-can SKU,
    // but a powder order is never less than 2 cans (also enforced in the
    // cart steppers, see js/cart-step.js).
    const floor = formulaKind === "powder" ? Math.max(minUnits || 0, POWDER_MIN_UNITS) : minUnits;
    return floor ? Math.max(rounded, floor) : rounded;
  }

  function smallestTierUnits(product, stage) {
    const tiers = product.stages[stage] && product.stages[stage].tiers;
    if (!tiers || !tiers.length) return undefined;
    return Math.min(...tiers.map((t) => t.units));
  }

  function monthsToStartWeek(months) {
    return Math.floor(months * MONTHS_TO_WEEKS_FACTOR);
  }

  function findWeekRow(product, weekNumber) {
    // Data is generated pre-sorted by week; a direct scan is fine at this size.
    return product.weeks.find((w) => w.week === weekNumber) || null;
  }

  // Past the last row of the feeding table the formula goes on being drunk (and
  // can be ordered for as long as wanted: comfort / anti-reflux are "from birth
  // onwards", and the last stage of every other product has no successor), so
  // later weeks simply use the last row's daily amount. Weeks BEFORE the table
  // starts still have no data.
  function rowForWeek(product, weekNumber) {
    const row = findWeekRow(product, weekNumber);
    if (row) return row;
    const maxWeek = Math.max(...product.weeks.map((w) => w.week));
    return weekNumber > maxWeek ? findWeekRow(product, maxWeek) : null;
  }

  function unitSizeForStage(product, stage) {
    const stageDef = product.stages[stage];
    if (!stageDef) return null;
    return stageDef.canGrams !== undefined ? stageDef.canGrams : stageDef.bottleMl;
  }

  function amountForDay(row, formulaKind, feedsPerDay) {
    if (formulaKind === "powder") {
      return row.scoopsPerFeed * row.scoopSizeG * feedsPerDay;
    }
    // RTF: some stages give only a flat ml/day total (Single feed (ml) blank
    // in the source data) - use that directly, ignoring the feeds/day input,
    // since there's no per-feed figure to scale.
    if (row.singleFeedMl === null || row.singleFeedMl === undefined) {
      return row.mlPerDayTypical;
    }
    return row.singleFeedMl * feedsPerDay;
  }

  /**
   * Suggests a typical feeds/day for the given product at the given age,
   * pulled from the data itself (not hardcoded) - used to pre-fill the
   * Feeds/Day stepper before the parent overrides it.
   */
  function suggestFeedsPerDay(product, ageMonths) {
    const week = monthsToStartWeek(ageMonths);
    const row = rowForWeek(product, week);
    if (!row) return null;
    if (row.feedsPerDayTypical !== null && row.feedsPerDayTypical !== undefined) {
      return row.feedsPerDayTypical;
    }
    return null;
  }

  /**
   * Day-by-day accumulation over the requested period. Skips days that fall
   * outside the data's age range entirely (never guesses/interpolates).
   */
  function computePlan(product, formulaKind, { ageMonths, feedsPerDay, weeksOfFormula, buffer = BUFFER }) {
    const startWeek = monthsToStartWeek(ageMonths);
    const totalDays = Math.round(weeksOfFormula * DAYS_PER_WEEK);

    const amountByStage = {};
    // Stage keys like "1"/"2"/"Pre" get reordered by the JS engine itself
    // when read back via Object.keys/Object.entries - integer-like string
    // keys always enumerate in ascending numeric order BEFORE any other
    // string key, regardless of insertion order (ECMAScript spec, not a
    // bug in this code). That silently put "Stage 2" ahead of "Stage Pre"
    // in the UI for products where Pre precedes a numbered stage. Track the
    // real chronological order explicitly instead of relying on object key
    // order anywhere downstream.
    const stageOrder = [];
    let endStage = null;
    let lastDayUsed = -1;

    for (let day = 0; day < totalDays; day++) {
      const weekNumber = startWeek + Math.floor(day / DAYS_PER_WEEK);
      const row = rowForWeek(product, weekNumber);
      if (!row) continue; // no data for this age - skip rather than estimate

      if (!(row.stage in amountByStage)) stageOrder.push(row.stage);
      amountByStage[row.stage] = (amountByStage[row.stage] || 0) + amountForDay(row, formulaKind, feedsPerDay);
      endStage = row.stage;
      lastDayUsed = day;
    }

    const unitsByStage = {};
    for (const stage of stageOrder) {
      const amount = amountByStage[stage];
      const unitSize = unitSizeForStage(product, stage);
      const withBuffer = amount * (1 + buffer);
      const rawUnits = unitSize ? Math.ceil(withBuffer / unitSize) : 0;
      unitsByStage[stage] = roundUnitsForPackaging(
        rawUnits, formulaKind, smallestTierUnits(product, stage), product.stages[stage].shrinkwrapSize
      );
    }

    return {
      startWeek,
      totalDays,
      amountByStage,
      unitsByStage,
      stageOrder,
      endStage,
      endWeek: lastDayUsed >= 0 ? startWeek + Math.floor(lastDayUsed / DAYS_PER_WEEK) : null,
      weeksCovered: lastDayUsed >= 0 ? Math.round(((lastDayUsed + 1) / DAYS_PER_WEEK) * 10) / 10 : 0,
    };
  }

  /**
   * "Best value": extends ONLY the stage the baby is in at the end of the
   * requested period, until its raw requirement reaches targetUnitsForEndStage
   * (the caller works out that target from the COMBINED pack total across all
   * stages reaching the next tier breakpoint - price-per-unit is based on the
   * total quantity, not any one stage in isolation). This keeps running the
   * SAME day-by-day simulation as computePlan, just for longer - so a stage
   * the baby has already aged out of naturally stops growing on its own
   * (no more days fall in it), while ageing into a NEW stage is allowed to
   * happen and contribute toward the target (the handoff doc's bug was an
   * earlier, already-finished stage growing past its own boundary - not
   * this: reaching a volume tier legitimately takes more weeks of supply,
   * which can mean more stages).
   */
  function computeBestValueOption(product, formulaKind, params, exactResult, targetTotalUnits) {
    if (!exactResult.endStage) return null;

    const buffer = params.buffer !== undefined ? params.buffer : BUFFER;
    const maxWeekInData = Math.max(...product.weeks.map((w) => w.week));
    // Past the last table row the plan goes on at that row's daily amount (see
    // rowForWeek), for up to two more years, so "best value" can always reach
    // the next price tier.
    const maxDays = Math.max(0, maxWeekInData - exactResult.startWeek + 1) * DAYS_PER_WEEK + EXTENSION_WEEKS * DAYS_PER_WEEK;

    const amountByStage = {};
    const stageOrder = []; // see computePlan - object key order is not chronological
    let unitsByStage = {};
    let combinedTotal = 0;
    let lastDayUsed = -1;

    for (let day = 0; day < maxDays && combinedTotal < targetTotalUnits; day++) {
      const weekNumber = exactResult.startWeek + Math.floor(day / DAYS_PER_WEEK);
      const row = rowForWeek(product, weekNumber);
      if (!row) continue; // no data for this age - skip, same as computePlan

      if (!(row.stage in amountByStage)) stageOrder.push(row.stage);
      amountByStage[row.stage] = (amountByStage[row.stage] || 0) + amountForDay(row, formulaKind, params.feedsPerDay);
      lastDayUsed = day;

      unitsByStage = {};
      combinedTotal = 0;
      for (const stage of stageOrder) {
        const amount = amountByStage[stage];
        const unitSize = unitSizeForStage(product, stage);
        const raw = Math.ceil((amount * (1 + buffer)) / unitSize);
        // Calculator orders are exact quantities for powder (a single-unit
        // SKU per stage - see cart mockup); RTF still rounds up to its own
        // shrinkwrap multiple (usually 6, 24 for 90ml lines - see
        // convert_data_v2.py's RTF_SHRINKWRAP_SIZE). Both then get floored
        // to the smallest variant actually sold (see roundUnitsForPackaging).
        unitsByStage[stage] = roundUnitsForPackaging(
          raw, formulaKind, smallestTierUnits(product, stage), product.stages[stage].shrinkwrapSize
        );
        combinedTotal += unitsByStage[stage];
      }
    }

    const weeksCovered = lastDayUsed >= 0
      ? Math.round(((lastDayUsed + 1) / DAYS_PER_WEEK) * 10) / 10
      : exactResult.weeksCovered;

    return {
      unitsByStage,
      stageOrder,
      weeksCovered,
      reachedTarget: combinedTotal >= targetTotalUnits,
    };
  }

  /**
   * Which volume-pricing tier the combined order total lands in: the index
   * of the largest tier breakpoint <= totalUnits (0 if below every
   * breakpoint - the smallest pack is still the base rate). Breakpoint
   * quantities (2/6/12/24) are a store-wide policy, but each stage/product
   * has its OWN price at a given index - never assume stage 1's and stage
   * 2's rate at the same bracket are equal.
   */
  function findTierBracketIndex(totalUnits, tierUnitsAscending) {
    let index = 0;
    for (let i = 0; i < tierUnitsAscending.length; i++) {
      if (tierUnitsAscending[i] <= totalUnits) index = i;
      else break;
    }
    return index;
  }

  global.FormulaEngine = {
    BUFFER,
    monthsToStartWeek,
    findWeekRow,
    rowForWeek,
    unitSizeForStage,
    suggestFeedsPerDay,
    computePlan,
    computeBestValueOption,
    findTierBracketIndex,
  };
})(typeof window !== "undefined" ? window : globalThis);
